import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { money, fmtDate, isValidPhone } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import OrderDetailDialog from "@/components/OrderDetailDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, Loader2, Eye, GitMerge, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", whatsapp: "", phone2: "", address: "", commune_id: "", quartier: "", reference: "" });
  const [orderDetailId, setOrderDetailId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupGroups, setDupGroups] = useState([]);
  const [dupLoading, setDupLoading] = useState(false);
  const [primaryByPhone, setPrimaryByPhone] = useState({});
  const [mergingPhone, setMergingPhone] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/clients${search ? `?search=${encodeURIComponent(search)}` : ""}`).then((r) => setClients(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search]);
  useEffect(() => { api.get("/communes").then((r) => setCommunes(r.data)); }, []);

  const communeName = (id) => communes.find((c) => c.id === id)?.name || "—";

  const openDetail = async (c) => {
    const { data } = await api.get(`/clients/${c.id}`);
    setDetail(data);
  };

  const save = async () => {
    if (!isValidPhone(form.whatsapp)) {
      toast.error("Numéro WhatsApp invalide. Format attendu : +243XXXXXXXXX");
      return;
    }
    try {
      await api.post("/clients", form);
      toast.success("Client créé");
      setShowForm(false);
      setForm({ name: "", whatsapp: "", phone2: "", address: "", commune_id: "", quartier: "", reference: "" });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const openEdit = () => {
    const c = detail.client;
    setEditForm({
      name: c.name || "", whatsapp: c.whatsapp || "", phone2: c.phone2 || "",
      address: c.address || "", commune_id: c.commune_id || "", quartier: c.quartier || "", reference: c.reference || "",
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!isValidPhone(editForm.whatsapp)) {
      toast.error("Numéro WhatsApp invalide. Format attendu : +243XXXXXXXXX");
      return;
    }
    setSavingEdit(true);
    try {
      const { data } = await api.put(`/clients/${detail.client.id}`, editForm);
      toast.success("Client mis à jour");
      setEditing(false);
      setDetail((d) => ({ ...d, client: { ...d.client, ...data } }));
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };

  const loadDuplicates = async () => {
    setDupLoading(true);
    try {
      const { data } = await api.get("/clients/duplicates");
      setDupGroups(data);
      const init = {};
      data.forEach((g) => { init[g.phone] = g.clients[0].id; });
      setPrimaryByPhone(init);
    } catch (e) { toast.error("Erreur de chargement des doublons"); }
    finally { setDupLoading(false); }
  };
  const openDup = () => { setDupOpen(true); loadDuplicates(); };
  const mergeGroup = async (g) => {
    const primary_id = primaryByPhone[g.phone];
    const duplicate_ids = g.clients.filter((c) => c.id !== primary_id).map((c) => c.id);
    setMergingPhone(g.phone);
    try {
      await api.post("/clients/merge", { primary_id, duplicate_ids });
      toast.success("Fiches fusionnées");
      await loadDuplicates();
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setMergingPhone(null); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div><h1 className="text-2xl sm:text-3xl font-bold font-heading">Clients</h1><p className="text-slate-500 text-sm mt-1">{clients.length} client(s)</p></div>
        <div className="flex items-center gap-2">
          <Button data-testid="clients-duplicates-btn" onClick={openDup} variant="outline"><GitMerge className="w-4 h-4 mr-2" /> Doublons</Button>
          <Button data-testid="clients-new-btn" onClick={() => setShowForm(true)} className="bg-brand-600 hover:bg-brand-700"><Plus className="w-4 h-4 mr-2" /> Nouveau client</Button>
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="clients-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nom, téléphone, référence..." className="pl-9" />
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div>
      ) : (
        <div className="bg-white border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Client</th>
                  <th className="text-left px-4 py-3 font-semibold">Téléphone</th>
                  <th className="text-left px-4 py-3 font-semibold">Commune</th>
                  <th className="text-right px-4 py-3 font-semibold">Commandes</th>
                  <th className="text-right px-4 py-3 font-semibold">Total dépensé</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr key={c.id} data-testid={`client-row-${c.id}`} onClick={() => openDetail(c)} className="border-t hover:bg-slate-50 cursor-pointer">
                    <td className="px-4 py-3 font-medium">
                      <div className="flex items-center gap-2">
                        {c.name}
                        {c.has_active_order && <span data-testid={`client-active-badge-${c.id}`} className="text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">En cours</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{c.whatsapp}</td>
                    <td className="px-4 py-3 text-slate-500">{communeName(c.commune_id)}</td>
                    <td className="px-4 py-3 text-right">{c.order_count || 0}</td>
                    <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">{money(c.total_spent)}</td>
                    <td className="px-4 py-3 text-right"><Eye className="w-4 h-4 text-slate-400 inline" /></td>
                  </tr>
                ))}
                {clients.length === 0 && <tr><td colSpan="6" className="text-center py-10 text-slate-400">Aucun client</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* New client */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Nouveau client</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Nom *</Label><Input data-testid="cf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div>
              <Label>WhatsApp *</Label>
              <Input data-testid="cf-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+243XXXXXXXXX" className={`mt-1 ${form.whatsapp && !isValidPhone(form.whatsapp) ? "border-red-400 ring-1 ring-red-300" : ""}`} />
              <p className={`text-xs mt-1 ${form.whatsapp && !isValidPhone(form.whatsapp) ? "text-red-600" : "text-slate-400"}`}>Format : +243 suivi de 9 chiffres</p>
            </div>
            <div><Label>Téléphone 2</Label><Input value={form.phone2} onChange={(e) => setForm({ ...form, phone2: e.target.value })} className="mt-1" /></div>
            <div><Label>Référence</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1" /></div>
            <div className="sm:col-span-2"><Label>Adresse</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" /></div>
            <div>
              <Label>Commune</Label>
              <Select value={form.commune_id} onValueChange={(v) => setForm({ ...form, commune_id: v })}>
                <SelectTrigger data-testid="cf-commune" className="mt-1"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>{communes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Quartier</Label><Input value={form.quartier} onChange={(e) => setForm({ ...form, quartier: e.target.value })} className="mt-1" /></div>
          </div>
          <div className="flex justify-end gap-2 mt-2"><Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button><Button data-testid="cf-submit" onClick={save} className="bg-brand-600 hover:bg-brand-700">Enregistrer</Button></div>
        </DialogContent>
      </Dialog>

      {/* Client detail + history */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between gap-3 pr-6">
                  <DialogTitle className="font-heading">{detail.client.name}</DialogTitle>
                  <Button data-testid="client-edit-btn" onClick={openEdit} variant="outline" size="sm"><Pencil className="w-4 h-4 mr-2" /> Modifier</Button>
                </div>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-3 text-sm bg-slate-50 rounded-xl p-4">
                <div>WhatsApp : {detail.client.whatsapp}</div>
                <div>Téléphone : {detail.client.phone2 || "—"}</div>
                <div>Commune : {communeName(detail.client.commune_id)}</div>
                <div>Quartier : {detail.client.quartier || "—"}</div>
                <div className="sm:col-span-2">Adresse : {detail.client.address || "—"}</div>
                <div>Référence : {detail.client.reference || "—"}</div>
                <div>Créé le : {fmtDate(detail.client.created_at)}</div>
                <div>Commandes : {detail.client.order_count || 0}</div>
                <div>Total dépensé : {money(detail.client.total_spent)}</div>
              </div>
              <h4 className="font-semibold text-sm mt-2">Historique des commandes</h4>
              <div className="space-y-2">
                {detail.orders.map((o) => (
                  <button key={o.id} onClick={() => { setDetail(null); setOrderDetailId(o.id); }} className="w-full text-left flex items-center justify-between bg-white border rounded-xl p-3 hover:bg-slate-50">
                    <div>
                      <div className="font-mono font-semibold text-brand-700">{o.order_number}</div>
                      <div className="text-xs text-slate-500">{fmtDate(o.created_at)} · {money(o.total)} · Payé {money(o.amount_paid)}</div>
                    </div>
                    <StatusBadge status={o.status} />
                  </button>
                ))}
                {detail.orders.length === 0 && <p className="text-sm text-slate-400 text-center py-3">Aucune commande</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <OrderDetailDialog orderId={orderDetailId} open={!!orderDetailId} onOpenChange={(v) => !v && setOrderDetailId(null)} />

      {/* Edit client */}
      <Dialog open={editing} onOpenChange={(v) => { if (!v) setEditing(false); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Modifier le client</DialogTitle></DialogHeader>
          {editForm && (
            <>
              <p className="text-xs text-slate-500">Le nom et le WhatsApp seront synchronisés sur les commandes en cours (l'historique des commandes terminées est préservé).</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Nom *</Label><Input data-testid="ce-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1" /></div>
                <div>
                  <Label>WhatsApp *</Label>
                  <Input data-testid="ce-whatsapp" value={editForm.whatsapp} onChange={(e) => setEditForm({ ...editForm, whatsapp: e.target.value })} placeholder="+243XXXXXXXXX" className={`mt-1 ${editForm.whatsapp && !isValidPhone(editForm.whatsapp) ? "border-red-400 ring-1 ring-red-300" : ""}`} />
                  <p className={`text-xs mt-1 ${editForm.whatsapp && !isValidPhone(editForm.whatsapp) ? "text-red-600" : "text-slate-400"}`}>Format : +243 suivi de 9 chiffres</p>
                </div>
                <div><Label>Téléphone 2</Label><Input value={editForm.phone2} onChange={(e) => setEditForm({ ...editForm, phone2: e.target.value })} className="mt-1" /></div>
                <div><Label>Référence</Label><Input value={editForm.reference} onChange={(e) => setEditForm({ ...editForm, reference: e.target.value })} className="mt-1" /></div>
                <div className="sm:col-span-2"><Label>Adresse</Label><Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="mt-1" /></div>
                <div>
                  <Label>Commune</Label>
                  <Select value={editForm.commune_id} onValueChange={(v) => setEditForm({ ...editForm, commune_id: v })}>
                    <SelectTrigger data-testid="ce-commune" className="mt-1"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent>{communes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Quartier</Label><Input value={editForm.quartier} onChange={(e) => setEditForm({ ...editForm, quartier: e.target.value })} className="mt-1" /></div>
              </div>
              <div className="flex justify-end gap-2 mt-2"><Button variant="outline" onClick={() => setEditing(false)}>Annuler</Button><Button data-testid="ce-submit" onClick={saveEdit} disabled={savingEdit} className="bg-brand-600 hover:bg-brand-700">{savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}</Button></div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Duplicate detection & merge */}
      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><GitMerge className="w-5 h-5 text-brand-600" /> Doublons de clients</DialogTitle></DialogHeader>
          {dupLoading ? (
            <div className="h-24 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div>
          ) : dupGroups.length === 0 ? (
            <p data-testid="dup-empty" className="text-sm text-slate-500 text-center py-6">Aucun doublon détecté 🎉</p>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Sélectionnez la fiche à conserver (principale). Les autres seront fusionnées dedans et leurs commandes réattribuées.</p>
              {dupGroups.map((g) => (
                <div key={g.phone} data-testid={`dup-group-${g.phone}`} className="border rounded-xl p-3 space-y-2">
                  <div className="text-xs font-mono text-slate-400">{g.phone} · {g.clients.length} fiches</div>
                  {g.clients.map((c) => (
                    <label key={c.id} className="flex items-center gap-3 text-sm bg-slate-50 rounded-lg px-3 py-2 cursor-pointer">
                      <input type="radio" name={`primary-${g.phone}`} checked={primaryByPhone[g.phone] === c.id} onChange={() => setPrimaryByPhone((p) => ({ ...p, [g.phone]: c.id }))} data-testid={`dup-primary-${c.id}`} />
                      <div className="flex-1">
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-slate-500">{c.order_count || 0} commande(s) · {money(c.total_spent)} · créé {fmtDate(c.created_at)}</div>
                      </div>
                    </label>
                  ))}
                  <Button data-testid={`dup-merge-${g.phone}`} onClick={() => mergeGroup(g)} disabled={mergingPhone === g.phone} size="sm" className="bg-brand-600 hover:bg-brand-700">
                    {mergingPhone === g.phone ? <Loader2 className="w-4 h-4 animate-spin" /> : <><GitMerge className="w-4 h-4 mr-2" /> Fusionner {g.clients.length - 1} fiche(s)</>}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
